/**
 * **示例数据**的构造与识别（批 `107` · K11 · B 域 PRD §5.4.1 / `AC-B4.2`）
 *
 * 依据：B 域 PRD **§5.4.1**（「示例数据：可选，**默认不注入**；用户主动『看示例』才显示」）·
 * `AC-B4.2` · `AC-B4.7`（空态必须给下一步动作）· 铁律 53（没入口 = 没兑现）。
 *
 * ## 🔴 三条设计决定
 *
 * | 决定 | 理由 |
 * |---|---|
 * | **标记 = 一个普通标签「示例数据」**，不是新列 / 新表 | 本批**零 schema**（业务表仍 43 / 迁移仍 17）。`transaction_tags` 是现成的**结构化**关联表 ⇒ 判据不靠备注文案（规格 §22.9 的精神） |
 * | **注入走 `POST /api/transactions`**（与手工记账同一入口） | 示例数据必须是**真数据**：走旁路 `insert` 就绕过了账户/分类归属校验与腿形态校验，示例本身可能是一份非法数据 —— 那比没有示例更糟 |
 * | **清除走 `bulk-delete`**（唯一删除接口） | 与用户自己删流水同一条路径 ⇒ 余额回滚、30 天可恢复、事件广播全部自动一致 |
 *
 * ## ⚠️ 为什么示例数据**不做"不计收支"**
 *
 * 示例的目的是让新人看懂"记了一笔会怎样"——若把示例全部标成不计收支，
 * 报表 / 预算 / 首页全是 0，示例就失去了示范意义。
 * ⇒ 示例按**真实记账**写入（唯一例外是那笔转账的两条腿，那本来就该不计收支）。
 * 风险（用户忘了清）由**常驻提示 + 一键清除**兜住，见 `JournalPage` 的示例横幅。
 */

import {
  AccountType,
  CAT,
  DEMO_DATA_TAG_NAME,
  addDays,
  type AccountView,
  type CategoryView,
  type CreateTransactionRequest,
  type TransactionRowView,
} from '@jiaying/shared';

/** 示例数据的标记标签名（唯一源在共享层 `contract/tag.ts`；本文件只转发，不重写一遍） */
export const DEMO_TAG = DEMO_DATA_TAG_NAME;

/** 注入示例所需的最小输入（全部来自**已有查询**，不额外发请求） */
export interface DemoPlanInput {
  readonly accounts: readonly AccountView[];
  readonly categories: readonly CategoryView[];
  /** 账本本地日（`YYYY-MM-DD`）—— 由调用方用 `localDayOf` 取，本文件不碰时钟 */
  readonly today: string;
}

/**
 * 一张示例清单 = 7 笔，覆盖四种形态。
 *
 * | # | 形态 | 让新人看到什么 |
 * |:-:|---|---|
 * | 1 | 收入（1 腿） | 工资进账、报表「收入」有数 |
 * | 2–4 | 支出（1 腿） | 分类归集、预算已用、首页支出 |
 * | 5 | **转账（2 腿 · 不计收支）** | 「不计收支」标注与合计条「另有 … 不计收支」 |
 * | 6 | 信用卡支出 | 卡面「本期应还」有数 |
 * | 7 | 今日支出 | 当天分组头（用户能看到"今天"这一栏） |
 *
 * 🔴 日期用 `addDays(today, -n)`（共享日历内核），**不碰 `Date`** ——
 * 账本时区 ≠ 浏览器时区时，自己 `new Date()` 会差一天（铁律：端侧一个日期都不算）。
 */
export function buildDemoTransactions(input: DemoPlanInput): CreateTransactionRequest[] {
  const usable = input.accounts.filter((a) => a.currency === baseCurrencyOf(input.accounts));
  const pick = (type: AccountType): AccountView | undefined => usable.find((a) => a.type === type);
  const debit = pick(AccountType.Debit) ?? usable.find((a) => a.type !== AccountType.Credit);
  const wallet = pick(AccountType.Ewallet) ?? usable.find((a) => a.id !== debit?.id);
  const credit = pick(AccountType.Credit);

  if (debit === undefined) return [];

  const has = (id: string): boolean => input.categories.some((c) => c.id === id);
  /** 只有分类真的在本账本里时才带上（自建分类的账本可能缺某个预设） */
  const cat = (id: string): string | null => (has(id) ? id : null);

  const out: CreateTransactionRequest[] = [];

  if (has(CAT.SALARY)) {
    out.push({
      date: addDays(input.today, -5),
      note: '本月工资（示例）',
      legs: [{ accountId: debit.id, direction: 'in', amount: 850_000, categoryId: CAT.SALARY }],
    });
  }

  out.push({
    date: addDays(input.today, -3),
    note: '午饭（示例）',
    legs: [{ accountId: debit.id, direction: 'out', amount: 4_500, categoryId: cat(CAT.MEAL) }],
  });
  out.push({
    date: addDays(input.today, -3),
    note: '地铁（示例）',
    legs: [{ accountId: debit.id, direction: 'out', amount: 1_200, categoryId: cat(CAT.TRANSIT) }],
  });

  if (wallet !== undefined) {
    out.push({
      date: addDays(input.today, -2),
      note: '买菜（示例）',
      legs: [{ accountId: wallet.id, direction: 'out', amount: 8_850, categoryId: cat(CAT.GROCERY) }],
    });
  }

  // 🔴 转账两腿都 `excludeFromStats`（`DR-004` / 服务端硬校验）
  if (wallet !== undefined) {
    out.push({
      date: addDays(input.today, -2),
      note: '转到钱包（示例）',
      legs: [
        { accountId: debit.id, direction: 'out', amount: 50_000, excludeFromStats: true },
        { accountId: wallet.id, direction: 'in', amount: 50_000, excludeFromStats: true },
      ],
    });
  }

  if (credit !== undefined) {
    out.push({
      date: addDays(input.today, -1),
      note: '网购（示例）',
      legs: [{ accountId: credit.id, direction: 'out', amount: 32_000, categoryId: cat(CAT.SHOPPING) }],
    });
  }

  out.push({
    date: input.today,
    note: '水电费（示例）',
    legs: [{ accountId: debit.id, direction: 'out', amount: 20_000, categoryId: cat(CAT.UTILITY) }],
  });

  return out;
}

/**
 * 示例清单的**基准币种** = 可用账户里出现最多的那种（拿不到就 `CNY`）。
 *
 * 🔴 为什么需要它：示例金额是**写死的人民币数**。若账本主账户是 USD，
 * 把 `¥45` 记进 USD 账户等于记了 45 美元 —— 数字不会报错，但含义全错。
 * ⇒ 只往**同币种**的账户里写；一个都没有就干脆不给示例（返回空清单）。
 */
function baseCurrencyOf(accounts: readonly AccountView[]): string {
  const seen = new Map<string, number>();
  for (const a of accounts) seen.set(a.currency, (seen.get(a.currency) ?? 0) + 1);
  let best = 'CNY';
  let bestN = 0;
  for (const [cur, n] of seen) {
    if (n > bestN) {
      best = cur;
      bestN = n;
    }
  }
  return best;
}

/**
 * 探测"账本里还有没有示例数据"的查询串（横幅判据的**取数**部分）。
 *
 * 🔴 为什么不从"当前列表里有没有带标记的行"判断：用户此刻的筛选可能正好把示例数据挡在外面
 * （选了某个账户 / 改了时间窗）⇒ 横幅会**凭空消失**，而示例数据还在账里。
 * ⇒ 用一次独立的、只按标签筛的极小请求（`pageSize=1`，只要 `total`）。
 */
export function demoProbeQuery(demoTagId: string, pageSize = 1): string {
  return `?tagIds=${encodeURIComponent(demoTagId)}&page=1&pageSize=${String(pageSize)}`;
}

/**
 * 当前列表里是否**已经有示例数据**（横幅判据）。
 *
 * 🔴 判据 = 行上有没有 `DEMO_TAG` 这个标签 id，**不看备注文案**（规格 §22.9：判据不靠文案）。
 * 端侧拿不到标签 id 时（标签还没建）恒为 `false` —— 那是"还没有示例数据"，不是未知。
 */
export function hasDemoRows(rows: readonly TransactionRowView[], demoTagId: string | null): boolean {
  if (demoTagId === null) return false;
  return rows.some((r) => r.tagIds.includes(demoTagId));
}
